/**
 * PostList.jsx
 * The two cards that list posts: what is going out next, and what is waiting
 * for a decision.
 *
 * They share a row — thumbnail, platform marks, caption, when — because they
 * are the same object at two points in its life, and giving them two different
 * shapes would suggest otherwise.
 *
 * Dependencies: helpers.js, services/api.js, utils/time.js
 */

import { Calendar, Check, Clock, Image, Layers, Plus, ChevronRight } from '../Icons.jsx';
import { api } from '../../services/api.js';
import { formatLondon } from '../../utils/time.js';
import { PLATFORMS, ago, platformList, snippet } from './helpers.js';

/** The picture a post carries, or a placeholder where it carries none. */
function Thumb({ mediaId, alt = '' }) {
  if (!mediaId) {
    return (
      <span className="post-row__thumb post-row__thumb--empty">
        <Image size={16} />
      </span>
    );
  }
  return (
    <img className="post-row__thumb" src={api.media.rawUrl(mediaId)} alt={alt} loading="lazy" />
  );
}

/** Which platforms a post reaches, as the platforms' own marks. */
export function PlatformMarks({ platforms }) {
  const list = platformList(platforms);
  if (!list.length) return null;
  return (
    <span className="platform-marks">
      {list.map((p) => {
        const { Icon: Mark, label, className } = PLATFORMS[p];
        return (
          <span className={`platform-mark ${className}`} key={p} title={label}>
            <Mark size={13} />
            <span className="sr-only">{label}</span>
          </span>
        );
      })}
    </span>
  );
}

/**
 * `badge` is optional on purpose. In a narrow column it costs about a third of
 * the caption, so a list whose heading already says what these posts are —
 * "Upcoming posts" — does without one.
 */
function PostRow({ post, when, badge = null, onClick }) {
  return (
    <button className="post-row" type="button" onClick={onClick}>
      <Thumb mediaId={post.media_id} />
      <span className="post-row__text">
        <span className="post-row__title">
          <PlatformMarks platforms={post.platforms} />
          {/* The caption needs its own box: text-overflow has nothing to
              truncate when it is a bare child of a flex container. */}
          <span className="post-row__caption">
            {snippet(post.caption) || <em>No caption yet</em>}
          </span>
        </span>
        <span className="post-row__meta">
          {post.brand_name} · {when}
        </span>
      </span>
      {badge}
    </button>
  );
}

// ============================================
// NEXT OUT
// ============================================

export default function UpcomingPosts({ posts = [], navigate }) {
  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Clock size={21} />
        </span>
        <div className="card__head-text">
          <h2>Upcoming posts</h2>
          <p>Your next scheduled content.</p>
        </div>
        <div className="card__head-end">
          <button className="btn btn--sm" onClick={() => navigate('/calendar')}>
            <Calendar size={15} /> View calendar
          </button>
        </div>
      </div>

      {posts.length === 0 ? (
        <div className="empty empty--inline">
          <div className="empty__art">
            <Calendar size={26} />
          </div>
          <h3>Nothing scheduled yet</h3>
          <p>Create a post and give it a time, and it will appear here.</p>
          <button className="btn btn--primary" onClick={() => navigate('/create')}>
            <Plus /> Create post
          </button>
        </div>
      ) : (
        <>
          <div className="post-list">
            {posts.map((p) => (
              <PostRow
                key={p.id}
                post={p}
                when={formatLondon(p.scheduled_at_utc)}
                onClick={() => navigate(`/create?post=${p.id}`)}
              />
            ))}
          </div>
          <button className="link-more" onClick={() => navigate('/calendar')}>
            View all scheduled posts <ChevronRight size={15} />
          </button>
        </>
      )}
    </section>
  );
}

// ============================================
// WAITING ON A DECISION
// ============================================

export function ApprovalQueue({ posts = [], total = 0, navigate }) {
  return (
    <section className="card">
      <div className="card__head">
        <span className={`tile ${total > 0 ? 'tile--warn' : 'tile--ok'}`}>
          {total > 0 ? <Layers size={21} /> : <Check size={21} />}
        </span>
        <div className="card__head-text">
          <h2>Approval queue</h2>
          <p>Drafts that nobody has said yes to yet.</p>
        </div>
        <div className="card__head-end">
          <button className="btn btn--sm" onClick={() => navigate('/drafts')}>
            View all
          </button>
        </div>
      </div>

      {posts.length === 0 ? (
        <div className="row">
          <span className="tile tile--sm tile--ok">
            <Check size={17} />
          </span>
          <div>
            <div className="row__label">Nothing waiting</div>
            <div className="row__sub">Every draft has been dealt with.</div>
          </div>
        </div>
      ) : (
        <>
          <div className="post-list">
            {posts.map((p) => (
              <PostRow
                key={p.id}
                post={p}
                when={ago(p.updated_at)}
                badge={<span className="badge badge--warn">Pending</span>}
                onClick={() => navigate(`/create?post=${p.id}`)}
              />
            ))}
          </div>
          {total > posts.length && (
            <button className="link-more" onClick={() => navigate('/drafts')}>
              {total - posts.length} more waiting <ChevronRight size={15} />
            </button>
          )}
        </>
      )}
    </section>
  );
}
